// src/utils/emailService.ts

import {  httpsCallable } from 'firebase/functions';
import { auth, functions } from '../config/firebase';

interface EmailTemplateData {
  to: string;
  templateId: string;
  dynamicData: {
    name: string;
    projectTitle: string;
    inviteUrl: string;
    invitedBy: string;
    role: string;
    expiresAt: string;
  };
}

interface EmailResponse {
  success: boolean;
  statusCode?: number;
  messageId?: string;
  error?: {
    message: string;
    code: string;
    details?: any;
  };
}

interface FirebaseFunctionsError extends Error {
  code?: string;
  details?: any;
}

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const sendEmailWithTemplate = async (data: EmailTemplateData): Promise<void> => {
  let lastError: FirebaseFunctionsError | null = null;
  
  for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
    try {
      // Ensure user is authenticated
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('Authentication required to send email. Please sign in and try again.');
      }

      // Ensure fresh token before each attempt
      await currentUser.getIdToken(true);
      
      const sendEmail = httpsCallable<EmailTemplateData, EmailResponse>(functions, 'sendEmail');
      const result = await sendEmail(data);

      if (!result.data.success) {
        throw new Error(result.data.error?.message || 'Failed to send email');
      }

      // If successful, exit retry loop
      return;
    } catch (error: any) {
      lastError = error as FirebaseFunctionsError;
      console.error(`Attempt ${attempt} failed:`, error);

      // Don't retry for certain errors
      if (
        error.code === 'functions/invalid-argument' ||
        error.code === 'functions/unauthenticated' ||
        error.code === 'functions/failed-precondition' ||
        attempt === MAX_RETRIES
      ) {
        break;
      }

      // Wait before retrying
      await wait(RETRY_DELAY * attempt);
    }
  }

  // If we get here, all attempts failed
  if (lastError) {
    // Format error message based on error type
    let errorMessage = 'Failed to send email after multiple attempts.';
    if (lastError.code === 'functions/unauthenticated') {
      errorMessage = 'Authentication required. Please sign in and try again.';
    } else if (lastError.code === 'functions/invalid-argument') {
      errorMessage = 'Invalid email data provided. Please check the email address and try again.';
    } else if (lastError.code === 'functions/failed-precondition') {
      errorMessage = 'Email service is not properly configured. Please contact support.';
    }

    throw lastError || errorMessage;
  }
};
